import React, { useState } from "react";
import { api, type Me } from "./api";
import { eastern, type Person } from "./RecordScreen";
import type { Note } from "./ReviewPanel";
import { HOLD_LABEL, HoldBadge } from "./ReviewQueue";

/**
 * Follow-up on one record (build plan §7.1 #6, #7):
 *   - document requests: the composer (two templates, fixed choices, a preview
 *     of the email exactly as it goes, with the family's private link shown
 *     as a placeholder), the requests sent, their reminders, "mark done";
 *   - the attendance hold: the System Admin's decision and consultation log.
 * Every action adds a note; the server checks every permission again.
 */

export interface DocRequest {
  noteKey: string;
  template: string;
  item: string;
  itemLabel: string;
  to: string[];
  sentUtc: string;
  sentByName: string;
  sentByEmail: string;
  status: "Open" | "Closed";
  closedUtc: string;
  closedByName: string;
  closedNote: string;
  reminders: { utc: string; period: number }[];
  days: number;
  overdue: boolean;
  staffNote: string;
}

export interface ComposerOptions {
  templates: string[];
  items: Record<string, { key: string; label: string }[]>;
  portalConfigured: boolean;
}

export interface HoldOptions {
  decisions: string[];
  methods: string[];
}

export interface FollowUpResult {
  person: Person;
  notes: Note[];
  docRequests: DocRequest[];
}

const TEMPLATE_LABEL: Record<string, string> = { upload: "Please upload a document", clarify: "Please check part of the application" };

function useRecordPost(personKey: string, onDone: (r: FollowUpResult) => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function run(path: string, body: unknown): Promise<boolean> {
    setBusy(true);
    setError(null);
    const r = await api<FollowUpResult>("POST", `/api/records/${encodeURIComponent(personKey)}/${path}`, body);
    setBusy(false);
    if (!r.ok) {
      setError(r.data.error ?? "That didn't save.");
      return false;
    }
    onDone(r.data);
    return true;
  }
  return { busy, error, setError, run };
}

function Composer({ personKey, options, onDone }: { personKey: string; options: ComposerOptions; onDone: (r: FollowUpResult) => void }) {
  const { busy, error, setError, run } = useRecordPost(personKey, onDone);
  const [template, setTemplate] = useState("upload");
  const [item, setItem] = useState("");
  const [staffNote, setStaffNote] = useState("");
  const [preview, setPreview] = useState<{ to: string[]; subject: string; previewHtml: string } | null>(null);
  const items = options.items[template] ?? [];

  async function showPreview() {
    setError(null);
    const r = await api<{ composed: { to: string[]; subject: string; previewHtml: string } }>("POST", `/api/records/${encodeURIComponent(personKey)}/doc-requests/preview`, { template, item });
    if (r.ok) setPreview(r.data.composed);
    else setError(r.data.error ?? "Couldn't build the email.");
  }

  if (!options.portalConfigured) {
    return <p className="text-sm text-amber-900">Document requests can't be sent yet: the portal address is not set for this app. Ask the owner.</p>;
  }
  return (
    <div className="rounded-md border border-slate-200 p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="dr-template" className="field-label">Template</label>
          <select id="dr-template" className="field-input" value={template} onChange={(e) => { setTemplate(e.target.value); setItem(""); setPreview(null); }}>
            {options.templates.map((t) => (
              <option key={t} value={t}>{TEMPLATE_LABEL[t] ?? t}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="dr-item" className="field-label">{template === "upload" ? "Document" : "Document or section"}</label>
          <select id="dr-item" className="field-input" value={item} onChange={(e) => { setItem(e.target.value); setPreview(null); }}>
            <option value="">Choose…</option>
            {items.map((i) => (
              <option key={i.key} value={i.key}>{i.label}</option>
            ))}
          </select>
        </div>
      </div>
      <label htmlFor="dr-note" className="field-label mt-3">Note for staff (optional; never emailed)</label>
      <input id="dr-note" className="field-input" value={staffNote} onChange={(e) => setStaffNote(e.target.value)} maxLength={1000} />
      <p className="field-hint">The email names the document and nothing else. It goes to the parent address(es) and the student, with the family's private link to their application.</p>
      {error && <p className="field-error" role="alert">{error}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" className="btn-secondary" disabled={!item || busy} onClick={showPreview}>Preview the email</button>
        {preview && (
          <button
            type="button"
            className="btn-primary"
            disabled={busy}
            onClick={async () => {
              if (await run("doc-requests", { template, item, staffNote })) {
                setPreview(null);
                setItem("");
                setStaffNote("");
              }
            }}
          >
            Send to the family
          </button>
        )}
      </div>
      {preview && (
        <div className="mt-3 rounded-md border border-slate-300 bg-slate-50 p-3 text-sm">
          <p className="break-words"><span className="text-slate-500">To:</span> {preview.to.join(", ")}</p>
          <p className="break-words"><span className="text-slate-500">Subject:</span> {preview.subject}</p>
          <iframe title="Email preview" sandbox="" srcDoc={preview.previewHtml} className="mt-2 h-72 w-full rounded border border-slate-200 bg-white" />
        </div>
      )}
    </div>
  );
}

function RequestRow({ personKey, r, canClose, onDone }: { personKey: string; r: DocRequest; canClose: boolean; onDone: (x: FollowUpResult) => void }) {
  const { busy, error, run } = useRecordPost(personKey, onDone);
  const [closing, setClosing] = useState(false);
  const [note, setNote] = useState("");
  return (
    <li className="py-3 text-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="min-w-0 break-words">
          <span className="font-medium">{r.template === "clarify" ? "Please check" : "Please upload"}: {r.itemLabel}</span>
          {r.status === "Open" ? (
            <span className={`ml-2 rounded px-1.5 py-0.5 text-xs font-medium ${r.overdue ? "bg-red-100 text-red-900" : "bg-sky-50 text-sky-900"}`}>
              Open · {r.days} day{r.days === 1 ? "" : "s"}{r.overdue ? " · overdue" : ""}
            </span>
          ) : (
            <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-xs font-medium text-emerald-900">Done</span>
          )}
        </p>
        {r.status === "Open" && canClose && !closing && (
          <button type="button" className="btn-secondary" onClick={() => setClosing(true)}>Mark done</button>
        )}
      </div>
      <p className="text-xs text-slate-500">
        Sent {eastern(r.sentUtc)} by {r.sentByName || r.sentByEmail} to <span className="break-all">{r.to.join(", ")}</span>
      </p>
      {r.reminders.length > 0 && (
        <p className="text-xs text-slate-500">Reminders: {r.reminders.map((x) => eastern(x.utc)).join("; ")}</p>
      )}
      {r.staffNote && <p className="text-xs text-slate-600">Staff note: {r.staffNote}</p>}
      {r.status === "Closed" && (
        <p className="text-xs text-slate-500">Marked done {eastern(r.closedUtc)} by {r.closedByName}{r.closedNote ? ` — ${r.closedNote}` : ""}</p>
      )}
      {closing && (
        <div className="mt-2">
          <label htmlFor={`close-${r.noteKey}`} className="field-label">How was it resolved? (optional)</label>
          <input id={`close-${r.noteKey}`} className="field-input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={4000} />
          {error && <p className="field-error" role="alert">{error}</p>}
          <div className="mt-2 flex gap-2">
            <button type="button" className="btn-primary" disabled={busy} onClick={async () => (await run(`doc-requests/${encodeURIComponent(r.noteKey)}/close`, { note })) && setClosing(false)}>
              Mark done
            </button>
            <button type="button" className="btn-secondary" onClick={() => setClosing(false)}>Cancel</button>
          </div>
        </div>
      )}
    </li>
  );
}

export function DocRequestsBox({
  me,
  person,
  requests,
  options,
  onDone,
}: {
  me: Me;
  person: Person;
  requests: DocRequest[];
  options: ComposerOptions;
  onDone: (r: FollowUpResult) => void;
}): React.ReactElement {
  const can = me.capabilities.includes("record.comment");
  const [composing, setComposing] = useState(false);
  const open = requests.filter((r) => r.status === "Open");
  return (
    <section className="section-card">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-base font-semibold">Document requests</h3>
        {can && !composing && (
          <button type="button" className="btn-secondary" onClick={() => setComposing(true)}>New request</button>
        )}
      </div>
      {person.familyUpdatedAfterReview && open.length > 0 && (
        <p className="mb-2 text-sm text-amber-900">The family has changed the application since the last review: check whether it answers an open request.</p>
      )}
      {composing && (
        <div className="mb-3">
          <Composer
            personKey={person.personKey}
            options={options}
            onDone={(r) => {
              setComposing(false);
              onDone(r);
            }}
          />
          <button type="button" className="mt-2 text-xs text-program underline" onClick={() => setComposing(false)}>Close the composer</button>
        </div>
      )}
      {requests.length === 0 ? (
        <p className="text-sm text-slate-600">None sent.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {[...requests].reverse().map((r) => (
            <RequestRow key={r.noteKey} personKey={person.personKey} r={r} canClose={can} onDone={onDone} />
          ))}
        </ul>
      )}
    </section>
  );
}

interface ConsultDraft {
  method: string;
  with: string;
  summary: string;
  onDate: string;
}

export function HoldBox({ me, person, options, onDone }: { me: Me; person: Person; options: HoldOptions; onDone: (r: FollowUpResult) => void }): React.ReactElement | null {
  const canDecide = me.capabilities.includes("hold.decide");
  const { busy, error, run } = useRecordPost(person.personKey, onDone);
  const [decision, setDecision] = useState("");
  const [conditions, setConditions] = useState("");
  const [summary, setSummary] = useState("");
  const [log, setLog] = useState<ConsultDraft[]>([]);
  const decided = person.attendanceHold && person.attendanceHold !== "None";
  if (!canDecide && !decided && !person.holdRequested) return null;

  const blankEntry = (): ConsultDraft => ({ method: options.methods[0] ?? "Call", with: "", summary: "", onDate: "" });
  const setEntry = (i: number, patch: Partial<ConsultDraft>) => setLog(log.map((e, j) => (j === i ? { ...e, ...patch } : e)));
  const ready = (decision ? summary.trim() && (decision !== "ClearedWithConditions" || conditions.trim()) : log.length > 0) && log.every((e) => e.with.trim() && e.summary.trim());

  return (
    <section className="section-card border-l-4 border-l-red-700">
      <h3 className="mb-1 text-base font-semibold">Attendance hold</h3>
      <p className="text-sm text-slate-700">
        {decided ? (
          <>
            <HoldBadge hold={person.attendanceHold} /> <span className="text-xs text-slate-500">decided {eastern(person.attendanceHoldDecidedAtUtc)} by {person.attendanceHoldDecidedByEmail}</span>
          </>
        ) : (
          "No decision yet."
        )}
      </p>
      {person.holdRequested && <p className="mt-1 text-sm font-medium text-red-900">Med staff have asked the Director to decide. Their reason is in the history below.</p>}
      {canDecide && (
        <div className="mt-3 rounded-md border border-slate-200 p-3">
          <p className="mb-2 text-sm font-medium">Consultation log</p>
          {log.map((e, i) => (
            <div key={i} className="mb-3 grid gap-2 border-b border-slate-100 pb-3 sm:grid-cols-4">
              <div>
                <label htmlFor={`c-method-${i}`} className="field-label">How</label>
                <select id={`c-method-${i}`} className="field-input" value={e.method} onChange={(ev) => setEntry(i, { method: ev.target.value })}>
                  {options.methods.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor={`c-with-${i}`} className="field-label">With whom</label>
                <input id={`c-with-${i}`} className="field-input" value={e.with} onChange={(ev) => setEntry(i, { with: ev.target.value })} maxLength={200} placeholder="e.g. parent, RN" />
              </div>
              <div>
                <label htmlFor={`c-date-${i}`} className="field-label">Date (optional)</label>
                <input id={`c-date-${i}`} type="date" className="field-input" value={e.onDate} onChange={(ev) => setEntry(i, { onDate: ev.target.value })} />
              </div>
              <div className="sm:col-span-4">
                <label htmlFor={`c-sum-${i}`} className="field-label">Summary</label>
                <textarea id={`c-sum-${i}`} className="field-input min-h-16" value={e.summary} onChange={(ev) => setEntry(i, { summary: ev.target.value })} maxLength={2000} />
                <button type="button" className="mt-1 text-xs text-program underline" onClick={() => setLog(log.filter((_x, j) => j !== i))}>Remove this entry</button>
              </div>
            </div>
          ))}
          <button type="button" className="btn-secondary" onClick={() => setLog([...log, blankEntry()])}>Add a call, email or meeting</button>

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="hold-decision" className="field-label">Decision</label>
              <select id="hold-decision" className="field-input" value={decision} onChange={(e) => setDecision(e.target.value)}>
                <option value="">No decision yet (log only)</option>
                {options.decisions.map((d) => (
                  <option key={d} value={d}>{HOLD_LABEL[d] ?? d}</option>
                ))}
              </select>
            </div>
            {decision === "ClearedWithConditions" && (
              <div>
                <label htmlFor="hold-conditions" className="field-label">Conditions</label>
                <input id="hold-conditions" className="field-input" value={conditions} onChange={(e) => setConditions(e.target.value)} maxLength={4000} />
              </div>
            )}
          </div>
          {decision && (
            <>
              <label htmlFor="hold-summary" className="field-label mt-3">Reason for the decision</label>
              <textarea id="hold-summary" className="field-input min-h-16" value={summary} onChange={(e) => setSummary(e.target.value)} maxLength={4000} />
            </>
          )}
          {error && <p className="field-error" role="alert">{error}</p>}
          <button
            type="button"
            className="btn-primary mt-3"
            disabled={busy || !ready}
            onClick={async () => {
              const body = { decision, conditions, summary, consultations: log.map((e) => ({ method: e.method, with: e.with, summary: e.summary, ...(e.onDate ? { onDate: e.onDate } : {}) })) };
              if (await run("hold", body)) {
                setDecision("");
                setConditions("");
                setSummary("");
                setLog([]);
              }
            }}
          >
            {decision ? "Record the decision" : "Save the log"}
          </button>
        </div>
      )}
    </section>
  );
}
